import { useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'

import { useLocales } from 'src/contexts'
import {
  CURRENT_PAGE_KEY,
  ITEM_PER_PAGE,
  ITEM_PER_PAGE_GRID,
} from 'src/constants'

import { useNavigation } from 'src/hooks/useNavigation'
import { useManageFuelHistory } from 'src/hooks/useManageFuelHistory'
import { useInputRefForFocus } from 'src/hooks/useInputRefFocus'
import { useDebounceState } from 'src/hooks/useDebounceState'
import { useAppSelector } from 'src/hooks/useAppSelector'
import { useAppDispatch } from 'src/hooks/useAppDispatch'
import { useIsMobile } from 'src/hooks/use-mobile'
import { fillArray } from 'src/helpers/fillArray'

import { Filters, SortStates } from 'src/pages/FuelTrackingHistory/types'
import {
  getCountOfActiveFilters,
  getListOfSortStates,
  handleSortChange,
} from 'src/pages/FuelTrackingHistory/helpers'
import { DEFAULT_FILTERS } from 'src/pages/FuelTrackingHistory/constants'

import { userSlice } from 'src/store/reducers/UserReducer/UserSlice'

export const useFuelTrackingHistory = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { translations } = useLocales()
  const isMobile = useIsMobile()
  const hiddenOnMobile = isMobile ? 'hidden' : ''

  const startPage = Number(searchParams.get(CURRENT_PAGE_KEY)) || 1

  const { goToMyCars } = useNavigation()

  const { allFuelHistory, isLoadingGetAllFuelHistory } = useManageFuelHistory()

  const dispatch = useAppDispatch()
  const { setIsPreferList } = userSlice.actions
  const { preferList, activeCar } = useAppSelector(state => state.user)

  const { inputRef } = useInputRefForFocus()

  const [
    searchText,
    submitSearchText,
    handleSearchChange,
    setCurrentDebouncedValue,
    setCurrentValue,
  ] = useDebounceState('')

  const [sortState, setSortState] = useState<SortStates>(SortStates.BY_NEW)
  const [currentPage, setCurrentPage] = useState<number>(startPage)
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS)

  const [isReversed, setIsReversed] = useState(false)
  const [isList, setIsList] = useState(!!preferList)

  const pageSeparator = isList ? ITEM_PER_PAGE : ITEM_PER_PAGE_GRID

  const filteredHistory = useMemo(() => {
    if (!allFuelHistory) return []

    return allFuelHistory.filter(item => {
      const matchesSearch = JSON.stringify(item)
        .toLowerCase()
        .includes(submitSearchText.toLowerCase())

      const itemOdometer = Number(item.odometer) || 0
      const itemPricePerLiter = Number(item.pricePerLiter) || 0
      const itemTotalCost = Number(item.totalCost) || 0

      const matchesOdometer =
        (filters.minOdometer === null || itemOdometer >= filters.minOdometer) &&
        (filters.maxOdometer === null || itemOdometer <= filters.maxOdometer)

      const matchesFuelQuantity =
        (filters.minFuelQuantity === null ||
          item.fuelQuantity >= filters.minFuelQuantity) &&
        (filters.maxFuelQuantity === null ||
          item.fuelQuantity <= filters.maxFuelQuantity)

      const matchesFuelType =
        !filters.fuelType || item.fuelType === filters.fuelType

      const matchesFuelPricePerLiter =
        (filters.minFuelPricePerLiter === null ||
          itemPricePerLiter >= filters.minFuelPricePerLiter) &&
        (filters.maxFuelPricePerLiter === null ||
          itemPricePerLiter <= filters.maxFuelPricePerLiter)

      const matchesTotalCost =
        (filters.minTotalCost === null ||
          itemTotalCost >= filters.minTotalCost) &&
        (filters.maxTotalCost === null || itemTotalCost <= filters.maxTotalCost)

      return (
        matchesSearch &&
        matchesOdometer &&
        matchesFuelQuantity &&
        matchesFuelType &&
        matchesFuelPricePerLiter &&
        matchesTotalCost
      )
    })
  }, [allFuelHistory, submitSearchText, filters])

  const sortedHistory = useMemo(() => {
    const baseArray = [...filteredHistory]

    const sorted = baseArray.sort(handleSortChange(sortState))

    return isReversed ? [...sorted].reverse() : sorted
  }, [filteredHistory, sortState, isReversed])

  const paginatedHistory = useMemo(() => {
    const start = (currentPage - 1) * pageSeparator

    return sortedHistory.slice(start, start + pageSeparator)
  }, [sortedHistory, currentPage, isList])

  const totalPages = useMemo(
    () => Math.ceil(sortedHistory.length / pageSeparator),
    [sortedHistory, isList],
  )

  const handleNextPage = (): void => {
    setCurrentPage(prevState => {
      if (prevState >= totalPages) return prevState

      const newPage = prevState + 1
      setSearchParams({ [CURRENT_PAGE_KEY]: newPage.toString() })

      return newPage
    })
  }

  const handlePreviousPage = (): void => {
    setCurrentPage(prevState => {
      if (prevState <= 1) return prevState

      const newPage = prevState - 1
      setSearchParams({ [CURRENT_PAGE_KEY]: newPage.toString() })

      return newPage
    })
  }

  const handleSetPage = (page: number): void => {
    setSearchParams({ [CURRENT_PAGE_KEY]: page.toString() })
    setCurrentPage(page)
  }

  const listOfSorts = useMemo(() => {
    return getListOfSortStates(translations.trackFuel.historyFuel)
  }, [translations])

  const handleChangeSortState = (value: SortStates): void => setSortState(value)

  const toggleReversed = (): void => {
    setIsReversed(prevState => !prevState)
  }

  const toggleListDisplay = (): void => {
    if (isMobile) return

    setIsList(prevIsList => {
      const newIsList = !prevIsList
      const newPageSeparator = newIsList ? ITEM_PER_PAGE : ITEM_PER_PAGE_GRID
      const newTotalPages = Math.max(
        1,
        Math.ceil(filteredHistory.length / newPageSeparator),
      )

      dispatch(setIsPreferList(Number(newIsList) || 0))

      setCurrentPage(prevPage => {
        const newPage = Math.min(prevPage, newTotalPages)

        setSearchParams({ [CURRENT_PAGE_KEY]: newPage.toString() })

        return newPage
      })

      return newIsList
    })
  }

  const clearInput = (): void => {
    setCurrentDebouncedValue('')
    setCurrentValue('')
  }

  useEffect(() => {
    if (!isMobile) return

    setIsList(true)
  }, [isMobile, isList])

  const breadcrumbItems = [
    { labelKey: translations.breadcrumbItems.home, href: '/' },
    {
      labelKey: translations.breadcrumbItems.fuelTracking,
      href: '/fuel',
    },
    { labelKey: translations.breadcrumbItems.history },
  ]

  return {
    locale: translations.trackFuel.historyFuel,
    currentCar: translations.currentCar,
    breadcrumbItems,
    searchText,
    handleSearchChange,
    allFuelHistory: paginatedHistory,
    isLoadingGetAllFuelHistory,
    sortState,
    handleChangeSortState,
    listOfSorts,
    isReversed,
    isList,
    toggleListDisplay,
    toggleReversed,
    handleNextPage,
    handlePreviousPage,
    handleSetPage,
    arrayPages: fillArray(totalPages),
    currentPage,
    clearInput,
    filters,
    setFilters,
    filtersCount: getCountOfActiveFilters(filters),
    inputRef,
    activeCar,
    goToMyCars,
    numberOfFoundItems: sortedHistory.length,
    hiddenOnMobile,
  }
}
